/**
 * @file useComicScroll.js
 * @description 漫画阅读页 — 滚动、进度追踪与恢复
 * @module features/comic/composables/useComicScroll
 */

import { syncReadProgressApi } from '../api/comicApi'
import { resolveProgressContextFromState } from '../utils/resolveProgressContext'
import { shouldPromptLoginForVol } from '../utils/volAccess'

/**
 * @param {Record<string, Function>} bridge 共享方法桥（避免循环依赖）
 * @param {object} ctx reactive 状态
 * @param {import('vue').Ref<HTMLElement|null>} readerContainerRef 阅读区滚动容器
 */
export function useComicScroll(bridge, ctx, readerContainerRef) {
  function getReaderContainer() {
    return (
      readerContainerRef?.value
      || ctx.$refs?.readerContainer
      || document.querySelector('.comic-reader-container')
    )
  }

  /** 等待阅读容器与图片 DOM 就绪（迁移后 ref 绑定可能晚于首个 nextTick） */
  async function waitForReaderReady(maxAttempts = 60) {
    for (let i = 0; i < maxAttempts; i++) {
      const container = getReaderContainer()
      if (container && ctx.pages.length > 0) {
        const domCount = container.querySelectorAll('.comic-image-container').length
        const refCount = ctx.imageRefs.filter(Boolean).length
        if (domCount > 0 || refCount > 0) {
          return container
        }
      }
      await ctx.$nextTick()
      await new Promise((resolve) => setTimeout(resolve, 16))
    }
    return getReaderContainer()
  }

  /** 在内部滚动容器内滚动到目标元素（scrollIntoView 对 overflow 容器不可靠） */
  function scrollToPageElement(targetElement, container) {
    if (!targetElement || !container) return
    const containerRect = container.getBoundingClientRect()
    const targetRect = targetElement.getBoundingClientRect()
    const offsetTop = targetRect.top - containerRect.top + container.scrollTop
    const top = offsetTop - (container.clientHeight - targetRect.height) / 2
    container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
  }

  function resolvePageElement(pageIndex, container) {
    let el = ctx.imageRefs[pageIndex]
    if (el) return el
    if (container) {
      el = container.querySelector(`[data-page-index="${pageIndex}"]`)
      if (el) {
        ctx.imageRefs[pageIndex] = el
        return el
      }
    }
    return null
  }
  function getRouteVolNum() {
    return typeof bridge.getActiveVolNum === 'function'
      ? bridge.getActiveVolNum()
      : Number(ctx.$route?.params?.volNum ?? ctx.routeVolNum ?? ctx.volNum)
  }

  function sumLoadedVolumeImages() {
    return ctx.loadedVolumes.reduce(
      (sum, vol) => sum + (vol.images?.length ?? 0),
      0,
    )
  }

  /** 根据全局图片索引，解析当前阅读卷及卷内 pageIndex */
  function resolveProgressContext(globalPageIndex) {
    return resolveProgressContextFromState(
      {
        comicId: ctx.comicId,
        volNum: ctx.volNum,
        routeVolNum: getRouteVolNum(),
        totalCount: ctx.totalCount,
        pages: ctx.pages,
        loadedVolumes: ctx.loadedVolumes,
        nextVolumePages: ctx.nextVolumePages,
        nextVolumeNum: ctx.nextVolumeNum,
      },
      globalPageIndex,
      getRouteVolNum(),
    )
  }

  /** 根据滚动位置更新当前页（IntersectionObserver 的补充，连续阅读下一卷时更可靠） */
  function updateProgressFromScrollPosition(container) {
    if (ctx.isRestoringProgress || !container) {
      return
    }

    const images = container.querySelectorAll('.comic-image-container[data-page-index]')
    if (!images.length) {
      return
    }

    const containerRect = container.getBoundingClientRect()
    const viewportCenter = containerRect.top + containerRect.height / 2

    let nearestImage = null
    let minDistance = Infinity

    images.forEach((imgContainer) => {
      const rect = imgContainer.getBoundingClientRect()
      if (rect.bottom < containerRect.top || rect.top > containerRect.bottom) {
        return
      }
      const imageCenter = rect.top + rect.height / 2
      const distance = Math.abs(imageCenter - viewportCenter)
      if (distance < minDistance) {
        minDistance = distance
        nearestImage = imgContainer
      }
    })

    if (nearestImage) {
      const pageIndex = parseInt(nearestImage.dataset.pageIndex || '0', 10)
      if (!Number.isNaN(pageIndex)) {
        updateCurrentPage(pageIndex)
      }
    }
  }

  function initProgressTracking() {
    if (ctx.isRestoringProgress) {
      return
    }

    const scrollRoot = getReaderContainer()
    if (!scrollRoot) {
      return
    }

    // 清理旧的 observer
    if (ctx.observer) {
      ctx.observer.disconnect()
    }

    // 创建 Intersection Observer
    // 当图片进入视口中心区域时，记录为当前阅读位置
    ctx.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 获取图片索引
            const pageIndex = parseInt(entry.target.dataset.pageIndex || '0', 10)
            // 只有当图片在视口中心区域时才更新（threshold 0.5 表示至少 50% 可见）
            if (entry.intersectionRatio >= 0.3) {
              updateCurrentPage(pageIndex)
            }
          }
        })
      },
      {
        root: scrollRoot,
        rootMargin: '-20% 0px -20% 0px', // 只检测视口中心 60% 的区域
        threshold: [0, 0.3, 0.5, 0.7, 1] // 多个阈值，更精确地检测
      }
    )

    // 观察所有画页（DOM 查询比稀疏 imageRefs 更可靠，尤其连续阅读下一卷时）
    scrollRoot.querySelectorAll('.comic-image-container[data-page-index]').forEach((el) => {
      ctx.observer.observe(el)
    })

    // 设置最后一个图片的监听器（用于自动加载下一卷）
    setupLastImageObserver()
  }

  function setupLastImageObserver() {
    if (ctx.isRestoringProgress) {
      return
    }

    const scrollRoot = getReaderContainer()
    if (!scrollRoot) {
      return
    }

    // 清理旧的 observer
    if (ctx.lastImageObserver) {
      ctx.lastImageObserver.disconnect()
      ctx.lastImageObserver = null
    }

    // 计算最后一个图片的索引（含 nextVolumePages 中正在加载的卷）
    const currentVolumeImageCount = ctx.pages.length
    const loadedVolumesImageCount = ctx.loadedVolumes.reduce((sum, vol) => {
      return sum + (vol.images ? vol.images.length : (vol.endIndex - vol.startIndex + 1))
    }, 0)
    const activeNextCount = ctx.nextVolumePages.length > loadedVolumesImageCount
      ? ctx.nextVolumePages.length - loadedVolumesImageCount
      : 0
    const totalImageCount = currentVolumeImageCount + loadedVolumesImageCount + activeNextCount

    // 如果没有图片，不设置 observer
    if (totalImageCount === 0) {
      return
    }

    // 从 DOM 找最后一张画页（比 imageRefs 更可靠）
    let lastImageElement = null
    let lastImageIndex = -1
    const allImages = scrollRoot.querySelectorAll('.comic-image-container[data-page-index]')
    if (allImages.length > 0) {
      lastImageElement = allImages[allImages.length - 1]
      lastImageIndex = parseInt(lastImageElement.dataset.pageIndex || '-1', 10)
    }

    // DOM 未就绪时回退到 imageRefs
    if (!lastImageElement && ctx.loadedVolumes.length > 0) {
      const lastVol = ctx.loadedVolumes[ctx.loadedVolumes.length - 1]
      if (lastVol.images && lastVol.images.length > 0) {
        // lastVol.endIndex 就是最后一个图片的索引
        lastImageIndex = lastVol.endIndex
        // 从 imageRefs 中找到对应的元素
        lastImageElement = ctx.imageRefs[lastImageIndex]
      }
    }

    // 如果没找到，检查当前卷的最后一个图片
    if (!lastImageElement && ctx.pages.length > 0) {
      lastImageIndex = ctx.pages.length - 1
      lastImageElement = ctx.imageRefs[lastImageIndex]
    }

    // 如果找到了最后一个图片元素，设置 observer
    if (lastImageElement) {
      // 清理旧的 observer
      if (ctx.lastImageObserver) {
        ctx.lastImageObserver.disconnect()
      }

      ctx.lastImageObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
              if (!ctx.allowAutoLoadAdjacentVolume) {
                                return
              }
                            // 最后一个图片进入视口，延迟一点时间确保 loadedVolumes 已更新，然后检查是否需要加载下一卷
              setTimeout(() => {
                                bridge.checkAndLoadNextVolume()
              }, 100)
            }
          })
        },
        {
            root: scrollRoot,
            rootMargin: '0px 0px -10% 0px', // 当图片距离底部 10% 时触发
            threshold: [0, 0.1, 0.3, 0.5, 1]
          }
        )

        ctx.lastImageObserver.observe(lastImageElement)
          }
  }

  function updateCurrentPage(pageIndex) {
    if (ctx.isRestoringProgress) {
      // 正在恢复进度时，不更新
      return
    }

    const progressCtx = resolveProgressContext(pageIndex)
    const routeVol = getRouteVolNum()
    const volChanged = Number(ctx.currentProgressVolNum) !== Number(progressCtx.volNum)

    if (ctx.currentPageIndex !== pageIndex || volChanged) {
      ctx.currentPageIndex = pageIndex

      if (volChanged) {
        ctx.currentProgressVolNum = progressCtx.volNum
        ctx.maxSavedPageIndex = -1
      }

      // 仅路由卷（pages）内滚动时触发分页加载
      if (progressCtx.volNum === routeVol && pageIndex < ctx.pages.length) {
        if (pageIndex >= ctx.loadNextPageThreshold - 1 && ctx.hasNext && !ctx.isLoading) {
          if (pageIndex > ctx.lastLoadTriggerIndex) {
            const remainingImages = ctx.pages.length - pageIndex - 1
            if (remainingImages <= 2) {
              ctx.lastLoadTriggerIndex = pageIndex
              bridge.loadNextPage()
            }
          }
        }
      }

      if (progressCtx.pageIndex > ctx.maxSavedPageIndex) {
        debouncedSaveProgress()
      }
    }
  }

  function debouncedSaveProgress() {
    if (ctx.saveProgressTimer) {
      clearTimeout(ctx.saveProgressTimer)
    }
    ctx.saveProgressTimer = setTimeout(() => {
      saveProgress()
    }, 500)
  }

  async function saveProgress(immediate = false) {
    if (ctx.pages.length === 0) {
      return
    }

    const progressCtx = resolveProgressContext(ctx.currentPageIndex)
    const { volNum, pageIndex, totalImages, progressKey } = progressCtx

    if (shouldPromptLoginForVol(ctx.volInfoVos, volNum, Boolean(ctx.token))) {
      return
    }

    if (pageIndex <= ctx.maxSavedPageIndex && !immediate) {
      return
    }

    const progress = totalImages > 0
      ? Math.min((pageIndex + 1) / totalImages, 1)
      : 0

    // 格式化时间
    const now = new Date()
    const lastReadTime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`

    const progressData = {
      comicId: ctx.comicId,
      volNum,
      pageIndex,
      progress,
      lastReadTime,
    }

    // 1. 先保存到 localStorage（快速响应，无需等待网络）
    try {
      const allProgress = JSON.parse(localStorage.getItem('reading_progress') || '{}')
      allProgress[progressKey] = progressData
      localStorage.setItem('reading_progress', JSON.stringify(allProgress))
      ctx.maxSavedPageIndex = pageIndex
      ctx.currentProgressVolNum = volNum
      if (typeof bridge.syncCatalogProgress === 'function') {
        bridge.syncCatalogProgress(volNum, pageIndex, progress)
      }
    } catch (error) {
      console.error('保存进度到 localStorage 失败:', error)
    }

    // 2. 如果有 token，异步同步到后端（不阻塞用户操作）
    if (ctx.token && !immediate) {
      syncProgressToServer(progressData)
        .then(() => {
          ctx.maxSavedPageIndex = pageIndex
          ctx.currentProgressVolNum = volNum
        })
        .catch((error) => {
          console.error('同步进度到服务器失败:', error)
        })
    } else if (ctx.token && immediate) {
      try {
        await syncProgressToServer(progressData)
        ctx.maxSavedPageIndex = pageIndex
        ctx.currentProgressVolNum = volNum
      } catch (error) {
        console.error('同步进度到服务器失败:', error)
      }
    }
  }

  async function syncProgressToServer(progressData) {
    try {
      await syncReadProgressApi(progressData, ctx.token)
    } catch (error) {
      // 网络错误时，数据已在 localStorage 中，不影响用户体验
      throw error
    }
  }

  async function restoreProgress() {
    const startTime = Date.now()
    const minLoadingTime = 800

    if (ctx.pages.length === 0) {
      return
    }

    ctx.isRestoringProgress = true

    try {
      let savedPageIndex = null

      // 1. 优先从路由 query 参数获取 pageIndex（从详情页跳转时传递）
      const routePageIndex = ctx.$route.query.pageIndex
      if (routePageIndex !== undefined) {
        const parsedIndex = parseInt(routePageIndex, 10)
        if (!isNaN(parsedIndex) && parsedIndex >= 0) {
          savedPageIndex = parsedIndex
        }
      }

      // 2. 如果路由参数没有，从 localStorage 获取进度
      if (savedPageIndex === null) {
        try {
          const allProgress = JSON.parse(localStorage.getItem('reading_progress') || '{}')
          const savedProgress = allProgress[ctx.progressKey]
          if (savedProgress && savedProgress.pageIndex !== undefined) {
            savedPageIndex = Math.max(0, savedProgress.pageIndex)
          }
        } catch (error) {
          console.error('从 localStorage 读取进度失败:', error)
        }
      }

      // TODO: 如果后端提供了获取进度的接口，可以在这里添加
      // 优先从后端获取，如果失败则使用 localStorage

      // 3. 如果有保存的进度，检查是否需要加载更多页
      if (savedPageIndex !== null) {
        const pageSize = Number(ctx.pageSize) || 10
        // 计算目标索引所在的页码（从1开始）
        const targetPageNum = Math.floor(savedPageIndex / pageSize) + 1

        // 如果目标索引超出了已加载的范围，需要先加载到对应的页
        if (savedPageIndex >= ctx.pages.length && ctx.hasNext) {
                    while (ctx.currentPageNum < targetPageNum && ctx.hasNext) {
            if (ctx.isLoading) {
              await new Promise((resolve) => setTimeout(resolve, 50))
              continue
            }
            await bridge.loadNextPage()
            await ctx.$nextTick()
          }
          await waitForImagesLoaded()
          await ctx.$nextTick()
        }

        const scrollContainer = await waitForReaderReady()
        if (!scrollContainer) {
          console.warn('📖 [恢复进度] 阅读容器未就绪，跳过恢复')
          return
        }

        // 限制目标索引在已加载的范围内
        const targetIndex = Math.min(savedPageIndex, ctx.pages.length - 1)

        let targetElement = resolvePageElement(targetIndex, scrollContainer)

        if (targetElement && scrollContainer) {
          ctx.currentPageIndex = targetIndex
          ctx.currentProgressVolNum = getRouteVolNum()
          ctx.maxSavedPageIndex = targetIndex

          // 计算已用时间，确保 loading 至少显示 minLoadingTime
          const elapsedTime = Date.now() - startTime
          const remainingTime = Math.max(0, minLoadingTime - elapsedTime)
          if (remainingTime > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingTime))
          }

          scrollToPageElement(targetElement, scrollContainer)
                    // 等待滚动动画完成后再隐藏 loading
          await new Promise(resolve => setTimeout(resolve, 500))
        } else {
          console.warn(`📖 [恢复进度] 未找到目标图片元素，索引: ${targetIndex}, imageRefs.length: ${ctx.imageRefs.length}`)
          // 确保 loading 至少显示 minLoadingTime
          const elapsedTime = Date.now() - startTime
          const remainingTime = Math.max(0, minLoadingTime - elapsedTime)
          if (remainingTime > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingTime))
          }
        }
      } else {
        // 没有保存的进度，重置到顶部
        resetScrollPosition()
        ctx.currentProgressVolNum = getRouteVolNum()
        ctx.maxSavedPageIndex = -1
        // 确保 loading 至少显示 minLoadingTime
        const elapsedTime = Date.now() - startTime
        const remainingTime = Math.max(0, minLoadingTime - elapsedTime)
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime))
        }
      }
    } catch (error) {
      console.error('恢复阅读进度失败:', error)
      resetScrollPosition()
      // 确保 loading 至少显示 minLoadingTime
      const elapsedTime = Date.now() - startTime
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime)
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }
    } finally {
      // 恢复完成后，重新初始化进度追踪
      ctx.$nextTick(() => {
        ctx.isRestoringProgress = false
        initProgressTracking()
      })
    }
  }

  function waitForImagesLoaded() {
    return new Promise((resolve) => {
      const root = getReaderContainer()
      const images = root ? root.querySelectorAll('.comic-image') : []
      let loadedCount = 0
      const totalImages = images.length

      if (totalImages === 0) {
        resolve()
        return
      }

      const checkComplete = () => {
        loadedCount++
        if (loadedCount >= totalImages) {
          // 额外等待一小段时间，确保布局稳定
          setTimeout(resolve, 100)
        }
      }

      images.forEach((img) => {
        if (img.complete) {
          checkComplete()
        } else {
          img.addEventListener('load', checkComplete, { once: true })
          img.addEventListener('error', checkComplete, { once: true })
        }
      })
    })
  }

  function resetScrollPosition() {
    const container = getReaderContainer()
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' })
    }
    ctx.currentPageIndex = 0
    // 重置已保存的最大 pageIndex
    ctx.maxSavedPageIndex = -1
    // 重置分页状态
    ctx.currentPageNum = 1
    ctx.lastLoadTriggerIndex = -1
  }

  function handleVisibilityChange() {
    if (document.hidden) {
      // 页面隐藏时保存进度
      saveProgress(true)
    }
  }

  function handleBeforeUnload() {
    // 页面卸载时立即保存进度
    saveProgress(true)
  }

  function detectCurrentDisplayVolume() {
    const container = getReaderContainer()
    if (!container) {
      return Number(ctx.volNum)
    }

    const scrollTop = container.scrollTop
    const containerHeight = container.clientHeight
    const viewportCenter = scrollTop + containerHeight / 2 // 视口中心位置

    // 获取所有图片容器
    const imageContainers = container.querySelectorAll('.comic-image-container')

    if (imageContainers.length === 0) {
      return Number(ctx.volNum)
    }

    // 找到视口中心位置附近的图片
    let nearestImage = null
    let minDistance = Infinity

    imageContainers.forEach((imgContainer) => {
      const rect = imgContainer.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      const imageTop = rect.top - containerRect.top + scrollTop
      const imageCenter = imageTop + rect.height / 2
      const distance = Math.abs(imageCenter - viewportCenter)

      if (distance < minDistance) {
        minDistance = distance
        nearestImage = imgContainer
      }
    })

    if (!nearestImage) {
      return Number(ctx.volNum)
    }

    // 从 data-vol-num 属性获取卷号
    const volNum = nearestImage.getAttribute('data-vol-num')
    if (volNum) {
      return Number(volNum)
    }

    // 如果没有 data-vol-num 属性，检查是否为互动区域附近的图片
    // 查找前面的互动区域，通过检查前面最近的互动区域的标题来判断
    let currentElement = nearestImage.previousElementSibling
    while (currentElement) {
      if (currentElement.classList.contains('interact-area')) {
        const titleElement = currentElement.querySelector('.comment-section-title')
        if (titleElement) {
          const titleText = titleElement.textContent || ''
          const match = titleText.match(/第(\d+)卷/)
          if (match && match[1]) {
            return Number(match[1])
          }
        }
      }
      currentElement = currentElement.previousElementSibling
    }

    // 默认返回当前卷号
    return Number(ctx.volNum)
  }

  function handleScroll(event) {
    if (ctx.pagesLocked) {
      return
    }
    const container = getReaderContainer()
    if (!container) {
      return
    }

    const currentScrollTop = container.scrollTop
    const scrollHeight = container.scrollHeight
    const clientHeight = container.clientHeight
    const scrollDelta = currentScrollTop - ctx.lastScrollTop

    if (!ctx.allowAutoLoadAdjacentVolume && Math.abs(scrollDelta) > 30) {
      ctx.allowAutoLoadAdjacentVolume = true
          }

    // 检测并更新当前显示的卷号
    const detectedVolNum = detectCurrentDisplayVolume()
    if (detectedVolNum !== ctx.currentDisplayVolNum) {
      ctx.currentDisplayVolNum = detectedVolNum
    }

    // 防抖处理
    if (ctx.scrollTimer) {
      clearTimeout(ctx.scrollTimer)
    }

    ctx.scrollTimer = setTimeout(() => {
      updateProgressFromScrollPosition(container)

      // 手指向上滑动（scrollTop 增加）：收起 chrome；向下滑动：展开 chrome
      if (Math.abs(scrollDelta) > 5) {
        if (scrollDelta > 0) {
          if (ctx.showTopBar || ctx.danmakuFabVisible) {
            ctx.showTopBar = false
            ctx.danmakuFabVisible = false
            if (ctx.showCatalog) {
              ctx.showCatalog = false
            }
          }

          // 检查是否需要加载下一卷
          // 条件：1. 当前卷已完成 2. 已滚动到底部或接近底部 3. 还没有触发加载 4. 不是正在加载中 5. 没有正在加载的下一卷
          const isNearBottom = currentScrollTop + clientHeight >= scrollHeight - 100 // 距离底部100px内
          const hasLoadedVolumes = ctx.loadedVolumes.length > 0
          const lastLoadedVolume = hasLoadedVolumes ? ctx.loadedVolumes[ctx.loadedVolumes.length - 1] : null
          const lastVolumeComplete = lastLoadedVolume && lastLoadedVolume.interactData !== null

          // 检查当前卷是否完成（如果是第一加载的卷）或者最后一个已加载卷是否完成
          const volumeComplete = ctx.isCurrentVolumeComplete || (hasLoadedVolumes && lastVolumeComplete)

          if (volumeComplete && isNearBottom && !ctx.scrollTriggeredLoadNext && !ctx.isLoadingNextVolume) {
                        ctx.scrollTriggeredLoadNext = true
            ctx.showLoadNextVolumePrompt = true
            ctx.loadVolumePromptText = '正在加载下一卷...'

              // 延迟1秒后开始加载（动画持续1秒）
            setTimeout(() => {
              ctx.showLoadNextVolumePrompt = false
              bridge.loadNextVolumeAfterScroll()
            }, 1000)
          }
        } else {
          // 手指向下滑动（scrollTop 减少）：展开 chrome
          if (!ctx.showTopBar || !ctx.danmakuFabVisible) {
            ctx.showTopBar = true
            ctx.danmakuFabVisible = true
          }

          // 检查是否需要加载上一卷
          // 条件：1. 当前卷已完成 2. 已滚动到顶部或接近顶部 3. 还没有触发加载 4. 不是正在加载中
          const isNearTop = currentScrollTop <= 100 // 距离顶部100px内
          const hasLoadedPrevVolumes = ctx.loadedPrevVolumes.length > 0
          const firstLoadedPrevVolume = hasLoadedPrevVolumes ? ctx.loadedPrevVolumes[0] : null
          const firstPrevVolumeComplete = firstLoadedPrevVolume && firstLoadedPrevVolume.interactData !== null

          // 检查当前卷是否完成（如果是第一加载的卷）或者第一个已加载上一卷是否完成
          const volumeComplete = ctx.isCurrentVolumeComplete || (hasLoadedPrevVolumes && firstPrevVolumeComplete)

          if (volumeComplete && isNearTop && !ctx.scrollTriggeredLoadPrev && !ctx.isLoadingPrevVolume) {
                        ctx.scrollTriggeredLoadPrev = true
            ctx.showLoadPrevVolumePrompt = true
            ctx.loadVolumePromptText = '正在加载上一卷...'

            // 延迟1秒后开始加载（动画持续1秒，1秒后自动隐藏提示并开始加载）
            setTimeout(() => {
              ctx.showLoadPrevVolumePrompt = false
              bridge.loadPrevVolumeAfterScroll()
            }, 1000)
          }
        }
      }

      ctx.lastScrollTop = currentScrollTop
    }, 20) // 20ms 防抖，提高响应速度
  }

  Object.assign(bridge, {
    initProgressTracking,
    setupLastImageObserver,
    updateCurrentPage,
    debouncedSaveProgress,
    saveProgress,
    syncProgressToServer,
    restoreProgress,
    waitForImagesLoaded,
    waitForReaderReady,
    resetScrollPosition,
    handleVisibilityChange,
    handleBeforeUnload,
    detectCurrentDisplayVolume,
    handleScroll,
  })
}
